import type { Invoice, InvoiceStatus } from "../types";
import { isPast } from "./format";

export const HIGH_VALUE_INVOICE_THRESHOLD = 50000;

export interface InvoiceTotals {
  subtotal: number;
  taxAmount: number;
  total: number;
  balance: number;
}

export function computeInvoiceTotals(invoice: Pick<Invoice, "lineItems" | "taxRate" | "amountPaid">): InvoiceTotals {
  const subtotal = invoice.lineItems.reduce((sum, li) => sum + li.quantity * li.unitPrice, 0);
  const taxAmount = Math.round(subtotal * (invoice.taxRate / 100));
  const total = subtotal + taxAmount;
  const balance = Math.max(0, total - invoice.amountPaid);
  return { subtotal, taxAmount, total, balance };
}

export function isInvoiceOverdue(invoice: Invoice, now: Date): boolean {
  if (invoice.status !== "Sent" && invoice.status !== "Partially Paid") return false;
  const { balance } = computeInvoiceTotals(invoice);
  return balance > 0 && isPast(invoice.dueDate, now);
}

/** Statusul afisat: adauga "Overdue" peste statusul stocat cand e cazul. */
export function effectiveInvoiceStatus(invoice: Invoice, now: Date): InvoiceStatus {
  if (isInvoiceOverdue(invoice, now)) return "Overdue";
  return invoice.status;
}

export function canSendInvoice(invoice: Invoice): { allowed: boolean; reason?: string } {
  if (invoice.status !== "Draft") {
    return { allowed: false, reason: "Only a draft invoice can be sent." };
  }
  const { total } = computeInvoiceTotals(invoice);
  if (total <= 0) {
    return { allowed: false, reason: "An invoice with a zero total cannot be sent. Add at least one line item." };
  }
  return { allowed: true };
}

export function canRecordPayment(invoice: Invoice): { allowed: boolean; reason?: string } {
  if (invoice.status === "Draft") {
    return { allowed: false, reason: "Send the invoice before recording a payment." };
  }
  if (invoice.status === "Paid") {
    return { allowed: false, reason: "This invoice is already fully paid." };
  }
  if (invoice.status === "Void") {
    return { allowed: false, reason: "A void invoice cannot receive payments." };
  }
  return { allowed: true };
}

export function canVoidInvoice(invoice: Invoice): { allowed: boolean; reason?: string } {
  if (invoice.status === "Paid") {
    return { allowed: false, reason: "A fully paid invoice cannot be voided." };
  }
  if (invoice.status === "Void") {
    return { allowed: false, reason: "This invoice is already void." };
  }
  return { allowed: true };
}

export function isHighValueInvoice(invoice: Invoice): boolean {
  return computeInvoiceTotals(invoice).total > HIGH_VALUE_INVOICE_THRESHOLD;
}

// ---------- Selectors ----------

export function invoicesForCompany(companyId: string, invoices: Invoice[]): Invoice[] {
  return invoices
    .filter((i) => i.companyId === companyId)
    .sort((a, b) => new Date(b.issueDate).getTime() - new Date(a.issueDate).getTime());
}

export function invoicesForOpportunity(opportunityId: string, invoices: Invoice[]): Invoice[] {
  return invoices.filter((i) => i.opportunityId === opportunityId);
}

/** Sold restant al facturilor emise (exclude Draft si Void). */
export function outstandingBalance(invoices: Invoice[]): number {
  return invoices
    .filter((i) => i.status === "Sent" || i.status === "Partially Paid")
    .reduce((sum, i) => sum + computeInvoiceTotals(i).balance, 0);
}

export function overdueAmount(invoices: Invoice[], now: Date): number {
  return invoices.filter((i) => isInvoiceOverdue(i, now)).reduce((sum, i) => sum + computeInvoiceTotals(i).balance, 0);
}

export function collectedAmount(invoices: Invoice[]): number {
  return invoices.filter((i) => i.status !== "Void").reduce((sum, i) => sum + i.amountPaid, 0);
}
